/**
 * DashboardDiffViewer — pick two dashboard.json snapshots, see what
 * changed between them.
 *
 * Snapshots come from R2 (worker writes one per day during the 8am
 * cron). The diff itself is computed server-side so we don't ship two
 * trimmed-but-still-large dashboard payloads to the browser.
 */
import { html, type TemplateResult } from "lit-html";
import { api } from "../data/api";
import type { DashboardDiff, DashboardDiffChange } from "../data/types";

interface ViewState {
  loadingList:    boolean;
  dates:          string[];
  listError:      string | null;
  a:              string | null;
  b:              string | null;
  diff:           DashboardDiff | null;
  diffLoading:    boolean;
  diffError:      string | null;
  filterKind:     "all" | "added" | "removed" | "changed";
  filterPath:     string;
}

const view: ViewState = {
  loadingList: true,
  dates:       [],
  listError:   null,
  a: null, b: null,
  diff:         null,
  diffLoading:  false,
  diffError:    null,
  filterKind:   "all",
  filterPath:   "",
};

let firstMount = true;

export function renderDashboardDiffViewer(onChange: () => void): TemplateResult {
  // Capture the latest onChange so internal buttons (filter chips, path
  // filter, snapshot-now) can request a repaint without re-threading it.
  _onChange = onChange;

  if (firstMount) {
    firstMount = false;
    bootstrap(onChange);
  }

  return html`
    <div class="diff-viewer">
      <header class="panel diff-viewer__head">
        <div>
          <h2 class="diff-viewer__title">Dashboard.json diff</h2>
          <span class="diff-viewer__sub">
            ${view.dates.length} snapshots ·
            ${view.dates.length > 0
              ? `${view.dates[0]} → ${view.dates[view.dates.length - 1]}`
              : "no snapshots yet"}
          </span>
        </div>
        <div class="diff-viewer__actions">
          <button class="diff-viewer__force-btn"
                  title="Force a snapshot now (bypasses cron)"
                  @click=${() => forceSnapshot(onChange)}>
            snapshot now
          </button>
        </div>
      </header>

      ${view.listError ? errorCard(view.listError) : ""}

      <section class="panel diff-viewer__pickers">
        ${pickerColumn("a", "left side (older)", onChange)}
        ${html`<div class="diff-viewer__arrow">→</div>`}
        ${pickerColumn("b", "right side (newer)", onChange)}
      </section>

      ${view.dates.length === 0 ? html`
        <div class="diff-viewer__empty panel">
          <strong>no snapshots yet</strong>
          The 8am ET cron writes one daily snapshot.
          Click <em>snapshot now</em> above to seed the first one.
        </div>
      ` : ""}

      ${diffSection()}
    </div>
  `;
}

function pickerColumn(slot: "a" | "b", caption: string, onChange: () => void): TemplateResult {
  const value = view[slot];
  return html`
    <label class="diff-viewer__picker">
      <span class="diff-viewer__picker-caption">${caption}</span>
      <select class="diff-viewer__select"
              .value=${value ?? ""}
              @change=${(e: Event) => {
                view[slot] = (e.target as HTMLSelectElement).value || null;
                fetchDiff(onChange);
              }}>
        <option value="" ?selected=${!value}>—</option>
        ${view.dates.map((d) => html`
          <option value=${d} ?selected=${d === value}>${d}</option>
        `)}
      </select>
    </label>
  `;
}

function diffSection(): TemplateResult {
  if (!view.a || !view.b) {
    return html`
      <div class="diff-viewer__empty panel">
        Select two snapshot dates above to see what changed.
      </div>
    `;
  }
  if (view.diffLoading) {
    return html`
      <div class="diff-viewer__empty panel">
        Computing diff…
      </div>
    `;
  }
  if (view.diffError) return errorCard(view.diffError);
  const d = view.diff;
  if (!d) return html``;

  const filtered = d.changes.filter((c) =>
    (view.filterKind === "all" || c.kind === view.filterKind) &&
    (!view.filterPath || c.path.toLowerCase().includes(view.filterPath.toLowerCase()))
  );

  return html`
    <section class="panel diff-viewer__summary">
      <header class="diff-viewer__summary-head">
        <h3>Summary · ${d.a} → ${d.b}</h3>
        <span class="diff-viewer__total">
          ${d.total_changes} change${d.total_changes === 1 ? "" : "s"}
          ${d.truncated ? html`<span class="diff-viewer__truncated">(truncated at 800)</span>` : ""}
        </span>
      </header>
      ${Object.keys(d.sport_summary).length === 0 ? html`
        <div class="diff-viewer__sport-empty">No bet-level differences in any sport.</div>
      ` : html`
        <div class="diff-viewer__sport-grid">
          ${Object.entries(d.sport_summary).map(([sport, s]) => html`
            <div class="diff-viewer__sport-card">
              <div class="diff-viewer__sport-name">${sport.toUpperCase()}</div>
              <div class="diff-viewer__sport-counts">
                ${s.added   ? html`<span class="diff-viewer__chip is-add">+${s.added}</span>` : ""}
                ${s.removed ? html`<span class="diff-viewer__chip is-rem">−${s.removed}</span>` : ""}
                ${s.changed ? html`<span class="diff-viewer__chip is-chg">~${s.changed}</span>` : ""}
              </div>
            </div>
          `)}
        </div>
      `}
    </section>

    <section class="panel diff-viewer__list">
      <header class="diff-viewer__list-head">
        <div class="diff-viewer__filter-group">
          ${(["all", "added", "removed", "changed"] as const).map((k) => html`
            <button class="diff-viewer__filter-btn ${view.filterKind === k ? "is-active" : ""}"
                    @click=${() => { view.filterKind = k; rerender(); }}>
              ${k}
            </button>
          `)}
        </div>
        <input class="diff-viewer__path-filter"
                type="search"
                placeholder="filter by path…"
                .value=${view.filterPath}
                @input=${(e: Event) => {
                  view.filterPath = (e.target as HTMLInputElement).value;
                  rerender();
                }}/>
      </header>
      ${filtered.length === 0 ? html`
        <div class="diff-viewer__empty">No matching changes.</div>
      ` : html`
        <ol class="diff-viewer__changes">
          ${filtered.map(renderChange)}
        </ol>
      `}
    </section>
  `;
}

function renderChange(c: DashboardDiffChange): TemplateResult {
  const symbol = c.kind === "added"   ? "+"
              : c.kind === "removed" ? "−"
              : "~";
  return html`
    <li class="diff-viewer__row diff-viewer__row--${c.kind}">
      <span class="diff-viewer__row-symbol">${symbol}</span>
      <code class="diff-viewer__row-path" title=${c.path}>${c.path}</code>
      <div class="diff-viewer__row-vals">
        ${c.kind !== "added" ? html`
          <pre class="diff-viewer__val is-before">${formatValue(c.before)}</pre>
        ` : ""}
        ${c.kind !== "removed" ? html`
          <pre class="diff-viewer__val is-after">${formatValue(c.after)}</pre>
        ` : ""}
      </div>
    </li>
  `;
}

function formatValue(v: any): string {
  if (v == null) return "null";
  if (typeof v === "string") return v.length > 200 ? v.slice(0, 200) + "…" : v;
  if (typeof v !== "object") return String(v);
  const j = JSON.stringify(v, null, 2);
  return j.length > 800 ? j.slice(0, 800) + "\n… (truncated)" : j;
}

function errorCard(msg: string): TemplateResult {
  return html`
    <div class="panel diff-viewer__error">
      <strong>error</strong> ${msg}
    </div>
  `;
}

// =============================================================
// Data
// =============================================================
async function bootstrap(onChange: () => void): Promise<void> {
  view.loadingList = true;
  try {
    const r = await api.dashboardSnapshots();
    view.dates = r.dates ?? [];
    if (view.dates.length >= 2) {
      view.b = view.dates[view.dates.length - 1] ?? null;
      view.a = view.dates[view.dates.length - 2] ?? null;
      void fetchDiff(onChange);
    }
  } catch (e) {
    view.listError = e instanceof Error ? e.message : String(e);
  } finally {
    view.loadingList = false;
    onChange();
  }
}

async function fetchDiff(onChange: () => void): Promise<void> {
  if (!view.a || !view.b || view.a === view.b) {
    view.diff = null;
    onChange();
    return;
  }
  view.diffLoading = true;
  view.diffError = null;
  onChange();
  try {
    const r = await api.dashboardDiff(view.a, view.b);
    view.diff = r;
  } catch (e) {
    view.diffError = e instanceof Error ? e.message : String(e);
  } finally {
    view.diffLoading = false;
    onChange();
  }
}

async function forceSnapshot(onChange: () => void): Promise<void> {
  try {
    await fetch("/api/dashboard-snapshots?force=1", { credentials: "include" });
    await bootstrap(onChange);
  } catch (e) {
    view.listError = e instanceof Error ? e.message : String(e);
    onChange();
  }
}

let _onChange: (() => void) | null = null;
function rerender(): void { _onChange?.(); }
